import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Heart, MapPin, Mountain, Truck, X, Calendar, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MapComponent from '../components/MapComponent';
import { locationsAPI, favoritesAPI, eventsAPI } from '../utils/api';
import { toast } from 'sonner';

const Locations = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const initialView = searchParams.get('view') === 'map' ? 'map' : 'grid';
  const [selectedCategories, setSelectedCategories] = useState(
    searchParams.get('categories')?.split(',').filter(Boolean) || []
  );
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [sortBy, setSortBy] = useState('price-asc');
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [locationsWithEvents, setLocationsWithEvents] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Filter buttons match the new multi-select Dostupnost field on /add-location.
  // `matches` patterns include legacy single values (chuze/offroad/dodavka) so old data still appears.
  const categories = [
    { id: 'dodavka', label: 'DODÁVKA', icon: Truck, matches: ['dodávka', 'dodavka'] },
    { id: '2wd', label: '2WD VŮZ', icon: Car, matches: ['2wd'] },
    { id: 'offroad', label: 'OFFROAD', icon: Mountain, matches: ['offroad'] },
    { id: 'pesky', label: 'PĚŠKY', icon: MapPin, matches: ['pěšky', 'pesky', 'chuze', 'chůze'] },
    { id: 'akce', label: 'AKCE', icon: Calendar },
  ];

  // Initial load: all locations + available facility tags + upcoming events
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [locResp, facResp, evResp] = await Promise.all([
          locationsAPI.getAll(),
          locationsAPI.getFacilities(),
          eventsAPI.listAll().catch(() => ({ data: [] })),
        ]);
        setLocations(locResp.data);
        setAvailableFacilities(facResp.data);
        setLocationsWithEvents(new Set((evResp.data || []).map((e) => e.location_id)));
      } catch (error) {
        console.error('Error fetching locations:', error);
        toast.error('Nepodařilo se načíst lokace');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load favorite ids (if logged in)
  const refreshFavorites = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setFavoriteIds(new Set());
      return;
    }
    try {
      const res = await favoritesAPI.listIds();
      setFavoriteIds(new Set(res.data));
    } catch (err) {
      console.error('Failed to load favorites', err);
    }
  }, []);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  // Re-fetch locations when facilities filter changes (server-side AND match)
  useEffect(() => {
    const fetchFiltered = async () => {
      try {
        const params = {};
        if (selectedFacilities.length > 0) {
          params.facilities = selectedFacilities.join(',');
        }
        const res = await locationsAPI.getAll(params);
        setLocations(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchFiltered();
  }, [selectedFacilities]);

  useEffect(() => {
    let filtered = [...locations];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(loc =>
        loc.name.toLowerCase().includes(q) ||
        loc.description.toLowerCase().includes(q)
      );
    }

    if (selectedCategories.length > 0) {
      const wantsEvents = selectedCategories.includes('akce');
      const otherCatIds = selectedCategories.filter((c) => c !== 'akce');
      // Build the union of all match patterns the user selected (case-insensitive).
      const patterns = otherCatIds.flatMap((cid) => {
        const c = categories.find((x) => x.id === cid);
        return c?.matches || [cid];
      });
      filtered = filtered.filter((loc) => {
        const matchEvents = !wantsEvents || locationsWithEvents.has(loc.id);
        const cat = (loc.category || '').toLowerCase();
        const matchCat = otherCatIds.length === 0 || patterns.some((p) => cat.includes(p));
        return matchEvents && matchCat;
      });
    }

    if (sortBy === 'price-asc') filtered.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-desc') filtered.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredLocations(filtered);
  }, [searchQuery, selectedCategories, sortBy, locations, locationsWithEvents]);

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleFacilityToggle = (name) => {
    setSelectedFacilities(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const clearFacilities = () => setSelectedFacilities([]);

  return (
    <div 
      className="min-h-screen"
    >
      <div className="relative">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 text-center">
            Prozkumnaj dostupná místa
          </h1>
          
          {/* Search and Filters */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Hledejte místo..."
                  className="pl-10 h-11"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-64 h-11" data-testid="sort-trigger">
                  <SelectValue placeholder="Seřadit podle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price-asc">Cena: Nejnižší</SelectItem>
                  <SelectItem value="price-desc">Cena: Nejvyšší</SelectItem>
                  <SelectItem value="name">Název: A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              {categories.map((category) => {
                const Icon = category.icon;
                const isSelected = selectedCategories.includes(category.id);
                return (
                  <label
                    key={category.id}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-orange-600 border-orange-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400'
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                      className="hidden"
                    />
                    <Icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{category.label}</span>
                  </label>
                );
              })}
            </div>

            {/* Amenities filter */}
            {availableFacilities.length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm text-gray-700">
                    Vybavení {selectedFacilities.length > 0 && (
                      <span className="text-orange-600">({selectedFacilities.length} vybráno)</span>
                    )}
                  </h3>
                  {selectedFacilities.length > 0 && (
                    <button
                      type="button"
                      onClick={clearFacilities}
                      className="text-xs text-gray-500 hover:text-orange-600 flex items-center gap-1"
                      data-testid="clear-facilities"
                    >
                      <X className="w-3 h-3" /> Vymazat
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableFacilities.map(({ name, count }) => {
                    const isSelected = selectedFacilities.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleFacilityToggle(name)}
                        data-testid={`facility-${name}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-orange-600 border-orange-600 text-white'
                            : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-orange-400'
                        }`}
                      >
                        <span>{name}</span>
                        <Badge className={`${isSelected ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'} text-[10px] px-1.5 py-0`}>
                          {count}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-white font-semibold bg-black/40 px-4 py-2 rounded-lg backdrop-blur-sm" data-testid="results-count">
            Zobrazeno <span className="font-bold">{filteredLocations.length}</span> míst
          </p>
        </div>

        <Tabs defaultValue={initialView} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="grid">Seznam</TabsTrigger>
            <TabsTrigger value="map">Mapa</TabsTrigger>
          </TabsList>

          <TabsContent value="grid">
            {loading ? (
              <div className="text-center py-20 text-white">Načítám…</div>
            ) : filteredLocations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLocations.map((location) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    isFavorite={favoriteIds.has(location.id)}
                    onFavoriteChange={refreshFavorites}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20" data-testid="no-results">
                <p className="text-2xl text-gray-500">Žádná místa nenalezena</p>
                <p className="text-gray-400 mt-2">Zkuste změnit vyhledávací kritéria</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="map">
            {filteredLocations.length > 0 ? (
              <MapComponent 
                locations={filteredLocations}
                height="600px"
                zoom={8}
              />
            ) : (
              <div className="text-center py-20">
                <p className="text-2xl text-gray-500">Žádná místa k zobrazení na mapě</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

const LocationCard = ({ location, isFavorite, onFavoriteChange }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info('Pro ukládání oblíbených míst se přihlaste');
      navigate('/login');
      return;
    }
    try {
      setBusy(true);
      if (isFavorite) {
        await favoritesAPI.remove(location.id);
        toast.success('Odebráno z oblíbených');
      } else {
        await favoritesAPI.add(location.id);
        toast.success('Přidáno do oblíbených ❤️');
      }
      onFavoriteChange?.();
    } catch (err) {
      console.error(err);
      toast.error('Nepodařilo se aktualizovat oblíbené');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="group relative overflow-hidden border border-transparent shadow-md transition-all duration-500 ease-out cursor-pointer rounded-xl hover:-translate-y-2 hover:scale-[1.02] hover:border-orange-400/60 hover:shadow-[0_18px_50px_-12px_rgba(249,115,22,0.55)]"
      data-testid={`location-card-${location.id}`}
      onClick={() => navigate(`/location/${location.id}`)}
    >
      {/* Animated orange glow behind the card on hover */}
      <div className="pointer-events-none absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl bg-gradient-to-br from-orange-500/25 via-amber-400/10 to-transparent -z-10" />
      <div 
        className="relative h-56 overflow-hidden"
        onClick={() => navigate(`/location/${location.id}`)}
      >
        <img
          src={location.photos?.[0] || location.image || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
          alt={location.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        {location.host_ready === false && (
          <div className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold shadow-md z-10">
            ⏳ Brzy dostupné
          </div>
        )}
        {location.no_caravans && (
          <div
            className={`absolute ${location.host_ready === false ? 'top-12' : 'top-3'} left-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md z-10`}
            data-testid={`no-caravans-badge-${location.id}`}
            title="Majitel neakceptuje karavany"
          >
            🚫 Bez karavanů
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleFavorite(e); }}
          disabled={busy}
          className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all z-10 disabled:opacity-60"
          data-testid={`favorite-btn-${location.id}`}
          aria-label={isFavorite ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
        >
          <Heart
            className={`w-5 h-5 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
          />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <div className="text-white font-semibold text-sm">{location.type}</div>
        </div>
      </div>
      <CardContent className="p-5">
        <h3 className="text-xl font-bold mb-2 group-hover:text-orange-600 transition-colors">
          {location.name}
        </h3>
        <p className="text-gray-600 mb-3 text-sm line-clamp-2">{location.description}</p>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <MapPin className="w-4 h-4" />
          <span>Divoká příroda</span>
        </div>
        <div className="flex items-center justify-end">
          <Button
            onClick={(e) => { e.stopPropagation(); navigate(`/location/${location.id}`); }}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Detail
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Locations;
